import type { ComponentProps, ReactNode } from "react";
import { Progress as ProgressPrimitive } from "@base-ui/react/progress";

import { cn } from "~/lib/utils";

type ProgressProps = Omit<ProgressPrimitive.Root.Props, "children"> & {
  children?: ReactNode;
};

type ProgressStepsBaseProps = Omit<
  ComponentProps<"div">,
  "children" | "role"
> & {
  max: number;
  value: number;
};

type ProgressStepsProps = ProgressStepsBaseProps &
  (
    | {
        getStepLabel?: never;
        isStepSelectable?: never;
        onStepSelect?: never;
      }
    | {
        getStepLabel: (index: number) => string;
        isStepSelectable?: (index: number) => boolean;
        onStepSelect: (index: number) => void;
      }
  );

function Progress({ className, children, value, ...props }: ProgressProps) {
  return (
    <ProgressPrimitive.Root
      value={value}
      data-slot="progress"
      className={cn("flex flex-wrap gap-3", className)}
      {...props}
    >
      {children}
      <ProgressTrack>
        <ProgressIndicator />
      </ProgressTrack>
    </ProgressPrimitive.Root>
  );
}

function ProgressTrack({ className, ...props }: ProgressPrimitive.Track.Props) {
  return (
    <ProgressPrimitive.Track
      className={cn(
        "control-surface-inset relative flex h-3 w-full items-center overflow-x-hidden rounded-full",
        className,
      )}
      data-slot="progress-track"
      {...props}
    />
  );
}

function ProgressIndicator({
  className,
  ...props
}: ProgressPrimitive.Indicator.Props) {
  return (
    <ProgressPrimitive.Indicator
      data-slot="progress-indicator"
      className={cn(
        "h-full bg-primary transition-[width,transform]",
        className,
      )}
      {...props}
    />
  );
}

function ProgressLabel({ className, ...props }: ProgressPrimitive.Label.Props) {
  return (
    <ProgressPrimitive.Label
      className={cn("text-meta font-medium", className)}
      data-slot="progress-label"
      {...props}
    />
  );
}

type ProgressValueProps = Omit<ProgressPrimitive.Value.Props, "children"> & {
  children?: ReactNode;
};

function ProgressValue({ children, className, ...props }: ProgressValueProps) {
  return (
    <ProgressPrimitive.Value
      className={cn(
        "ml-auto text-meta text-muted-foreground tabular-nums",
        className,
      )}
      data-slot="progress-value"
      {...props}
    >
      {typeof children === "undefined" ? undefined : () => children}
    </ProgressPrimitive.Value>
  );
}

/** A compact finite-flow indicator that can optionally navigate completed steps. */
function ProgressSteps({
  "aria-describedby": ariaDescribedBy,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  className,
  getStepLabel,
  isStepSelectable,
  max,
  onStepSelect,
  value,
  ...props
}: ProgressStepsProps) {
  const normalizedMax = Number.isFinite(max) ? Math.max(1, Math.trunc(max)) : 1;
  const normalizedValue = Number.isFinite(value)
    ? Math.min(normalizedMax, Math.max(0, Math.trunc(value)))
    : 0;

  const steps = Array.from({ length: normalizedMax }, (_, index) => {
    const visual = (
      <span
        aria-hidden="true"
        className="block size-1.5 shrink-0 rounded-full bg-muted-foreground/25 transition-[background-color,transform] duration-80 data-[complete=true]:bg-foreground"
        data-complete={index < normalizedValue}
        data-slot="progress-step"
        key={index}
      />
    );

    if (!onStepSelect) {
      return visual;
    }

    const selectable = isStepSelectable?.(index) ?? index < normalizedValue;
    if (!selectable) {
      return (
        <span
          aria-hidden="true"
          className="grid h-6 w-4 shrink-0 place-items-center rounded-full"
          data-slot="progress-step-control"
          key={index}
        >
          {visual}
        </span>
      );
    }

    return (
      <button
        aria-label={getStepLabel(index)}
        className="group/progress-step grid h-6 w-4 shrink-0 cursor-pointer place-items-center rounded-full border-0 bg-transparent p-0 outline-none glider-focus-ring"
        data-slot="progress-step-control"
        key={index}
        onClick={() => onStepSelect(index)}
        type="button"
      >
        {visual}
      </button>
    );
  });

  if (onStepSelect) {
    return (
      <div
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className={cn(
          "control-surface-inset inline-flex h-8 w-fit items-center justify-center gap-1.5 rounded-full px-3",
          className,
        )}
        data-interactive="true"
        data-slot="progress-steps"
        role="group"
        {...props}
      >
        {steps}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "control-surface-inset inline-flex h-8 w-fit items-center justify-center gap-1.5 rounded-full px-3",
        className,
      )}
      data-slot="progress-steps"
      {...props}
    >
      <progress
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className="sr-only"
        max={normalizedMax}
        value={normalizedValue}
      />
      {steps}
    </div>
  );
}

export {
  Progress,
  ProgressTrack,
  ProgressIndicator,
  ProgressLabel,
  ProgressSteps,
  ProgressValue,
};
